import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {
  ActiveCellEditorModel,
  computeActiveCellEditor,
  computeButtonFormatting,
  computeIsSelectionOnlyDatetimeCells,
  computeLinkButtonDisabled,
  computeSelectionAlignment,
  computeSelectionLink,
  computeSelectionPrecision,
  MergeButtonFormatting,
  SelectionLink,
} from 'notebook/tables/store/logic/TableActiveCellLogic';
import {computeIsRealIndex} from 'notebook/tables/store/logic/TableSelectionLogic';
import {TableSelectionModel} from 'notebook/tables/store/selectors/tableModel/TableSelectionSelectors';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {AlignmentState} from 'notebook/tables/toolbar/FormulaBox';
import {Optional} from 'util/Types';

/**
 * Model containing formula-bar / table-controls state for the currently active
 * cell.
 *
 */
export interface TableActiveCellModel {
  /**
   * Whether the "add link" button is disabled. A link may only be set when
   * exactly one cell is selected.
   */
  linkButtonDisabled: boolean;

  /**
   * The link (url + text) of the active cell, or an empty object when there is
   * no linkable selection.
   */
  link: SelectionLink;

  /**
   * The shared precision across all rendered selected cells, or `null` when
   * there is no selection or the selected cells have differing precisions.
   */
  precision: Optional<number>;

  /**
   * The alignment state (explicit `active` alignment and rendered `default`
   * alignment) for the current selection.
   */
  alignment: AlignmentState;

  /**
   * Whether the current selection contains only datetime cells. Used to gate
   * the "insert now" datetime action in the table controls.
   */
  isDatetimeCell: boolean;

  /**
   * The state (`active` / `disabled`) of the "merge cells" button in the table
   * controls for the current selection.
   */
  buttonFormatting: MergeButtonFormatting;

  /**
   * The evaluation error of the active cell, shown in the formula bar. `null` when there is no
   * real selection.
   */
  error: Optional<EvaluatedCellError>;

  /**
   * The row metadata of the active cell's row, or `null` when there is no active cell.
   */
  rowMetadata: RowMetadata | null;

  /**
   * How many of the selected columns support a precision setting. `0` when there is no real
   * selection.
   */
  numColumnsSupportingPrecision: number;

  /**
   * The values the floating cell editor renders with while the active cell is being edited, or
   * `null` when no cell is being edited or the edited cell cannot render yet.
   */
  editor: ActiveCellEditorModel | null;
}

/**
 * Creates a memoized selector for the active cell's editor values. Its inputs are the edited
 * cell's own data, so it only recomputes when that cell changes, not when any other cell does.
 */
function createActiveCellEditorSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps
): (store: TableEditableStore) => ActiveCellEditorModel | null {
  const editingIndex = (store: TableEditableStore): TableIndex | null =>
    store.stableProps.multiEditing ? store.state.multiSelIndex : null;
  return createTableStoreSelector(
    'activeCellEditor',
    {
      index: editingIndex,
      isRowEvaluated: (store) => {
        const index = editingIndex(store);
        return index != null && volatilePropsSelector(store).computedTable[index[0]] != null;
      },
      isRealIndex: (store) => computeIsRealIndex(editingIndex(store), volatilePropsSelector(store).table),
      evaluatedCell: (store) => {
        const index = editingIndex(store);
        return index == null ? undefined : volatilePropsSelector(store).computedTable[index[0]]?.[index[1]];
      },
      rowData: (store) => {
        const index = editingIndex(store);
        return index == null ? null : (volatilePropsSelector(store).table.rowData?.[index[0]] ?? null);
      },
      columnConstraint: (store) => {
        const index = editingIndex(store);
        return index == null ? null : volatilePropsSelector(store).table.columnConstraints[index[1]];
      },
    },
    ({index, isRowEvaluated, isRealIndex, evaluatedCell, rowData, columnConstraint}) =>
      index == null
        ? null
        : computeActiveCellEditor({
            rowIndex: index[0],
            colIndex: index[1],
            isRowEvaluated,
            isRealIndex,
            evaluatedCell,
            rowData,
            columnConstraint,
          })
  );
}

/**
 * Creates a memoized selector that computes the TableActiveCellModel from the store.
 */
export function createTableActiveCellSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  tableSelectionSelector: (store: TableEditableStore) => TableSelectionModel,
  boundaryIndexSelector: (store: TableEditableStore) => number | null
): (store: TableEditableStore) => TableActiveCellModel {
  return createTableStoreSelector(
    'tableActiveCell',
    {
      multiStartIndex: (store) => store.state.multiStartIndex,
      multiEndIndex: (store) => store.state.multiEndIndex,
      multiSelIndex: (store) => store.state.multiSelIndex,
      table: (store) => volatilePropsSelector(store).table,
      computedTable: (store) => volatilePropsSelector(store).computedTable,
      hasRealSelection: (store) => tableSelectionSelector(store).hasRealSelection,
      expandedBox: (store) => tableSelectionSelector(store).expandedBox,
      singleInputBoundaryIndex: boundaryIndexSelector,
      allowMergeSelection: (store) => store.stableProps.allowMergeSelection,
      editor: createActiveCellEditorSelector(volatilePropsSelector),
    },
    ({
      multiStartIndex,
      multiEndIndex,
      multiSelIndex,
      table,
      computedTable,
      hasRealSelection,
      expandedBox,
      singleInputBoundaryIndex,
      allowMergeSelection,
      editor,
    }) => {
      const linkButtonDisabled = computeLinkButtonDisabled(multiStartIndex, multiEndIndex);
      return {
        linkButtonDisabled,
        link: computeSelectionLink(hasRealSelection, linkButtonDisabled, multiStartIndex, table),
        precision: computeSelectionPrecision(table, computedTable, expandedBox, hasRealSelection),
        alignment: computeSelectionAlignment(table, computedTable, expandedBox, hasRealSelection),
        isDatetimeCell: computeIsSelectionOnlyDatetimeCells(
          table.columnConstraints,
          expandedBox,
          hasRealSelection
        ),
        buttonFormatting: computeButtonFormatting(
          hasRealSelection,
          multiStartIndex,
          multiEndIndex,
          singleInputBoundaryIndex,
          TableUtil.getFreezeBoundary(table),
          computedTable,
          allowMergeSelection ?? undefined
        ),
        error:
          hasRealSelection && multiSelIndex != null
            ? computedTable[multiSelIndex[0]]?.[multiSelIndex[1]]?.error
            : null,
        rowMetadata: multiSelIndex ? (table.rowData?.[multiSelIndex[0]]?.rowMetadata ?? null) : null,
        numColumnsSupportingPrecision:
          hasRealSelection && expandedBox != null
            ? TableUtil.getNumberOfColumnsSupportingPrecision(table, expandedBox.minJ, expandedBox.maxJ)
            : 0,
        editor,
      };
    }
  );
}
